/* eslint-disable max-lines */
import axios from 'common/requester';
import { API, LINB_API } from 'common/constants/api-urls';
import { PM_PROVIDERS } from 'common/constants/constants';
import _ from 'lodash';
import Icon from 'components/base/Icon';
import { LineSkeleton } from 'components/base/Skeletons';
import styled from 'styled-components';
import { FILTER_TYPES } from 'common/constants/filters';
import { getEntities } from 'common/services/pm-projects-filters.service';

const StyledMenuLoaderLine = styled.div`
  width: 85%;
  margin-top: 1.1rem;
`;

const PmEntitiesSyncInProgresExtra = () => {
  return (
    <StyledMenuLoaderLine>
      <LineSkeleton size={100} />
    </StyledMenuLoaderLine>
  );
};

const PmEntitiesSyncFailedExtra = () => {
  return <Icon name='ErrorIcon' color='radicalRed' size='medium' />;
};

export const PM_ENTITY_SYNC_FAILED_MESSAGE = {
  message:
    'An error occurred while trying to load configuration options from your Jira instance, please contact LinearB Support.',
  extra: <PmEntitiesSyncFailedExtra />
};
export const PM_ENTITIES_SYNC_IN_PROGRESS_MESSAGE = {
  message:
    'We are still loading configuration options from your Jira instance. Please try again in a couple of minutes.',
  extra: <PmEntitiesSyncInProgresExtra />
};

export const PM_ENTITIES_NO_DATA_MESSAGE = {
  message: 'No data to show.'
};

export const FREE_CHILD_MESSAGE = {
  message: 'Use Enter for multiple values'
};

export const mapProviderEntityToUrl = {
  [PM_PROVIDERS.JIRA]: {
    [FILTER_TYPES.BOARD]: LINB_API.PM_SERVICE.PM_ENTITIES.BOARDS,
    [FILTER_TYPES.LABEL]: LINB_API.PM_SERVICE.PM_ENTITIES.LABELS,
    [FILTER_TYPES.EPIC]: LINB_API.PM_SERVICE.PM_ENTITIES.EPICS,
    [FILTER_TYPES.PROJECTS]: LINB_API.PM_SERVICE.PM_ENTITIES.PROJECTS,
    [FILTER_TYPES.FIELD]: LINB_API.PM_SERVICE.PM_ENTITIES.FIELDS,
    [FILTER_TYPES.ISSUE_TYPES]: LINB_API.PM_SERVICE.PM_ENTITIES.ISSUE_TYPES,
    [FILTER_TYPES.FIX_VERSION]: LINB_API.PM_SERVICE.PM_ENTITIES.FIX_VERSIONS,
    [FILTER_TYPES.ISSUE_PARENT_1]: LINB_API.PROJET_PROGRESSION.ISSUES,
    [FILTER_TYPES.ISSUE_PARENT_2]: LINB_API.PROJET_PROGRESSION.ISSUES
  },
  [PM_PROVIDERS.CLUBHOUSE]: {
    [FILTER_TYPES.PROJECTS]: LINB_API.PM_SERVICE.ENTITIES.SHORTCUT.PROJECTS,
    [FILTER_TYPES.TEAMS]: LINB_API.PM_SERVICE.ENTITIES.SHORTCUT.TEAMS,
    [FILTER_TYPES.LABEL]: LINB_API.PM_SERVICE.ENTITIES.SHORTCUT.LABELS,
    [FILTER_TYPES.EPIC]: LINB_API.PM_SERVICE.ENTITIES.SHORTCUT.EPICS
  },
  [PM_PROVIDERS.AZURE_BOARDS]: {
    [FILTER_TYPES.PROJECTS]: LINB_API.PM_SERVICE.PM_ENTITIES.PROJECTS,
    [FILTER_TYPES.LABEL]: LINB_API.PM_SERVICE.PM_ENTITIES.LABELS,
    [FILTER_TYPES.FIELD]: LINB_API.PM_SERVICE.PM_ENTITIES.FIELDS,
    [FILTER_TYPES.TEAMS]: LINB_API.PM_SERVICE.ENTITIES.AZURE_BOARDS.TEAMS,
    [FILTER_TYPES.ISSUE_TYPES]: LINB_API.PM_SERVICE.PM_ENTITIES.ISSUE_TYPES,
    [FILTER_TYPES.EPIC]: LINB_API.PM_SERVICE.PM_ENTITIES.EPICS,
    [FILTER_TYPES.ISSUE_PARENT_1]: LINB_API.PROJET_PROGRESSION.ISSUES,
    [FILTER_TYPES.ISSUE_PARENT_2]: LINB_API.PROJET_PROGRESSION.ISSUES
  }
};

export const DEFAULT_SUPPORTED_FIELDS = {
  [PM_PROVIDERS.JIRA]: ['string', 'option', 'option2', 'account'],
  [PM_PROVIDERS.AZURE_BOARDS]: [
    'string',
    'plainText',
    'integer',
    'double',
    'dateTime',
    'boolean',
    'treePath'
  ]
};

export const DEFAULT_LIMIT_PAGE_SIZE = 100;
export const DEFAULT_PM_ENTITIES_OFFSET = 0;

export const getEntitiesCounterMapperByKey = (entities = [], key = 'value') => {
  const mapKeyToCount = entities.reduce((acc, entity) => {
    return {
      ...acc,
      [entity?.[key]?.toLowerCase()]:
        Number(acc[entity?.[key]?.toLowerCase()] || 0) + 1
    };
  }, {});
  return mapKeyToCount;
};

export const getPdtProjects = async () => {
  try {
    const res = await axios.get(
      LINB_API.PM_SERVICE.PM_ENTITIES.PDT_PROJECT_FILTERS
    );
    const formattedPdtProjects = (res || [])
      .map(pdtProject => ({
        id: pdtProject.id,
        title: pdtProject.name,
        entity: pdtProject
      }))
      .filter(({ id, title }) => !!id && !!title);
    return formattedPdtProjects;
  } catch (e) {
    return [];
  }
};

export const getIssuesEntities = async ({
  keyword = '',
  entityType,
  pmSensorAuthId,
  sensorId,
  limit = DEFAULT_LIMIT_PAGE_SIZE,
  offset = DEFAULT_PM_ENTITIES_OFFSET
} = {}) => {
  const mapEntityTypeToPayloadType = {
    [PM_PROVIDERS.JIRA]: {
      [FILTER_TYPES.ISSUE_PARENT_1]: 'epic',
      [FILTER_TYPES.ISSUE_PARENT_2]: 'initiative'
    },
    [PM_PROVIDERS.AZURE_BOARDS]: {
      [FILTER_TYPES.ISSUE_PARENT_1]: 'feature',
      [FILTER_TYPES.ISSUE_PARENT_2]: 'epic'
    }
  };

  try {
    const qsParams = {
      ...(keyword && { keyword }),
      ...(!!pmSensorAuthId && { sensor_authorization_id: pmSensorAuthId }),
      entity_type: mapEntityTypeToPayloadType[sensorId][entityType],
      limit,
      ...(offset > 0 && { offset })
    };
    const url = mapProviderEntityToUrl[PM_PROVIDERS.JIRA][entityType];
    if (!url) {
      return [];
    }

    const response = await axios.post(url, qsParams);
    const items = response?.items || [];
    const formattedEntities = items
      .map(item => {
        const isDataLegal =
          (!!item?.issue_provider_key || !!item?.issue_summary) &&
          !!item.issue_provider_id;

        const issueKey = item?.issue_provider_key || '';
        const issueSummary = item?.issue_summary || '';
        const title = `${issueKey} - ${issueSummary}`;

        return isDataLegal
          ? { id: item.issue_provider_id, title, entity: item }
          : null;
      })
      .filter(Boolean);

    return formattedEntities;
  } catch (e) {
    return [];
  }
};

export const getDynamicIssuesEntities = async ({
  pmSensorAuthId,
  entityType,
  issueType,
  keyword,
  limit,
  offset
} = {}) => {
  try {
    const entities = await getEntities({
      sensor_authorization_id: pmSensorAuthId,
      entity_type: issueType,
      keyword,
      limit,
      offset
    });
    const formattedEntities = entities.items.map(entity => ({
      id: entity.issue_provider_id || entity.issue_provider_key,
      title: entity.issue_summary,
      entity_type: issueType
    }));
    return formattedEntities;
  } catch (e) {
    return [];
  }
};

export const getJiraIssueTypesItems = async ({
  keyword = '',
  entityType,
  limit = DEFAULT_LIMIT_PAGE_SIZE,
  offset = DEFAULT_PM_ENTITIES_OFFSET
} = {}) => {
  const items = await getJiraItems({ keyword, entityType, limit, offset });
  return _.uniqBy(items, 'title');
};

export const getJiraItems = async ({
  keyword = '',
  entityType,
  limit = DEFAULT_LIMIT_PAGE_SIZE,
  offset = DEFAULT_PM_ENTITIES_OFFSET,
  pmSensorAuthId
} = {}) => {
  const qsParams = {
    limit,
    ...(!!keyword && { keyword }),
    ...(!!pmSensorAuthId && { sensor_authorization_id: pmSensorAuthId }),
    ...(offset > 0 && { pagination_cursor: offset })
  };

  const url = mapProviderEntityToUrl[PM_PROVIDERS.JIRA][entityType];

  if (!url) {
    return [];
  }

  const response = await axios.get(`${url}?${new URLSearchParams(qsParams)}`);
  const { items } = response;

  const formattedEntities = _.map(items, entity => {
    const entityProjectName = entity?.extra?.project_name;

    return {
      id: entity.entity_id,
      title: entity.value,
      ...(entityProjectName && { hoverText: entityProjectName }),
      entity
    };
  });
  return formattedEntities;
};
export const getShortcutItems = async ({
  entityType,
  pmSensorAuthId,
  keyword = ''
} = {}) => {
  const url = mapProviderEntityToUrl[PM_PROVIDERS.CLUBHOUSE][entityType];

  const qsParams = {
    sensor_authorization_id: pmSensorAuthId
  };
  const response = await axios.get(`${url}?${new URLSearchParams(qsParams)}`);

  const entities = response?.items || response.entities;
  const formattedEntities = entities.map(entity => ({
    id: entity.entity_id,
    title: entity.entity_name,
    entity,
    ...(entity.extra?.project_name && { hoverText: entity.extra?.project_name })
  }));
  const filteredEntities = formattedEntities.filter(entity =>
    entity.title.toLowerCase().includes(keyword.toLowerCase())
  );
  return filteredEntities;
};
export const getAzureBoardsItems = async ({
  entityType,
  keyword = '',
  pmSensorAuthId,
  offset = DEFAULT_PM_ENTITIES_OFFSET
} = {}) => {
  const qsParams = {
    limit: DEFAULT_LIMIT_PAGE_SIZE,
    ...(!!keyword && { keyword }),
    ...(offset > 0 && { pagination_cursor: offset }),
    ...(pmSensorAuthId && { sensor_authorization_id: pmSensorAuthId })
  };
  const url = mapProviderEntityToUrl[PM_PROVIDERS.AZURE_BOARDS][entityType];

  const response = await axios.get(`${url}?${new URLSearchParams(qsParams)}`);
  const entities = response?.items || response.entities;
  const formattedEntities = entities.map(entity => ({
    id: entity.entity_id,
    title: entity.extra?.title || entity.value || entity.entity_name,
    entity,
    ...(entity.extra?.project_name && { hoverText: entity.extra?.project_name })
  }));
  return formattedEntities;
};

const getNonCustomFieldsCondition = (field, sensorId) => {
  const conditionByProvider = {
    [PM_PROVIDERS.AZURE_BOARDS]:
      field?.extra?.custom === false &&
      ['Microsoft.VSTS.Common.Priority', 'System.AreaId'].includes(
        field?.entity_id
      ),
    [PM_PROVIDERS.JIRA]: field?.extra?.custom === false
  };
  return conditionByProvider[sensorId];
};

/**
 * filterSupportedTypesFields is filtering out custom fields that are not of one of the supported types
 * that been sent in the supportedTypes
 * example for supported types: supportedType = ['string', 'option'] *
 * @param {*} fields
 * @param {string[]} supportedTypes
 * @param {boolean} supportAllFields
 * @param {string} sensorId
 * @returns {any}
 */
export const filterSupportedTypesFields = (
  fields = [],
  supportedTypes = [],
  supportAllFields = false,
  sensorId
) => {
  const listKeyByProvider = {
    [PM_PROVIDERS.JIRA]: 'array',
    [PM_PROVIDERS.AZURE_BOARDS]: 'picklist'
  };

  if (supportAllFields) {
    return fields;
  }

  return fields.filter(field => {
    const { extended_data: extendedData, extra } = field;
    const type = extra?.extended_data?.type || extendedData?.type;
    const items = extra?.extended_data?.items || extendedData?.items;

    return (
      supportedTypes.includes(type) ||
      (supportedTypes.includes(items) &&
        type === listKeyByProvider[sensorId]) ||
      getNonCustomFieldsCondition(field, sensorId)
    );
  });
};

// return with all custom fields of all types according to searchQuery
export const getAllCustomFields = async ({
  keyword = '',
  sensorId = PM_PROVIDERS.JIRA,
  limit = 5000,
  offset = DEFAULT_PM_ENTITIES_OFFSET
} = {}) => {
  const qsParams = {
    ...(!!keyword && { keyword }),
    limit,
    ...(offset > 0 && { pagination_cursor: offset })
  };
  const url = mapProviderEntityToUrl[sensorId][FILTER_TYPES.FIELD];
  const { items } =
    (await axios.get(`${url}?${new URLSearchParams(qsParams)}`)) ?? {};
  return items;
};

export const getCustomFieldsItems = async ({
  keyword = '',
  supportedTypes = DEFAULT_SUPPORTED_FIELDS,
  supportAllFields,
  sensorId,
  limit = 1000,
  offset = DEFAULT_PM_ENTITIES_OFFSET
} = {}) => {
  const allCustomFields = await getAllCustomFields({
    keyword,
    sensorId,
    limit,
    offset
  });
  const filteredAndFormattedFields = filterSupportedTypesFields(
    allCustomFields,
    supportedTypes,
    supportAllFields,
    sensorId
  ).map(({ entity_id, value, extra }) => ({
    id: entity_id,
    title: value,
    ...(extra?.project_name && { hoverText: extra.project_name })
  }));
  return filteredAndFormattedFields;
};

export const filterTypesMapper = {
  [PM_PROVIDERS.AZURE_BOARDS]: {
    [FILTER_TYPES.FIELD]: 'Fields',
    [FILTER_TYPES.LABEL]: 'Tags',
    [FILTER_TYPES.ISSUE_TYPES]: 'Work Items',
    [FILTER_TYPES.FIELDS_DEFINITIONS]: 'Fields',
    [FILTER_TYPES.ISSUE_PARENT_1]: 'Features',
    [FILTER_TYPES.ISSUE_PARENT_2]: 'Epics',
    [FILTER_TYPES.ORGANIZATION_TEAMS]: 'LinearB Teams',
    [FILTER_TYPES.ORGANIZATION_PDT]: 'LinearB Projects'
  },
  [PM_PROVIDERS.JIRA]: {
    [FILTER_TYPES.FIELD]: 'Custom Fields',
    [FILTER_TYPES.LABEL]: 'Labels',
    [FILTER_TYPES.ISSUE_TYPES]: 'Issue Types',
    [FILTER_TYPES.FIELDS_DEFINITIONS]: 'Custom Fields',
    [FILTER_TYPES.ISSUE_PARENT_1]: 'Epics',
    [FILTER_TYPES.ISSUE_PARENT_2]: 'Initiatives',
    [FILTER_TYPES.ORGANIZATION_TEAMS]: 'LinearB Teams',
    [FILTER_TYPES.ORGANIZATION_PDT]: 'LinearB Projects'
  }
};

export const getNoDataMessage = async selectedFilterType => {
  const filterTypeToJobsSuiteNameMapper = {
    [FILTER_TYPES.LABEL]: 'SyncPMLabels',
    [FILTER_TYPES.BOARD]: 'SyncPMBoards',
    [FILTER_TYPES.ISSUE_TYPES]: 'SyncPMIssueTypes',
    [FILTER_TYPES.STATUSES]: 'SyncPMStatuses',
    [FILTER_TYPES.EPIC]: 'SyncPMEpics',
    [FILTER_TYPES.PROJECTS]: 'SyncPMProjects',
    [FILTER_TYPES.FIELD]: 'SyncPMFields'
  };
  const { jobs } = await axios.get(API.JOBS_SUITES_PATH, {
    params: {
      jobs_suite_name: filterTypeToJobsSuiteNameMapper[selectedFilterType]
    }
  });
  const relevantJob = jobs && jobs[0];
  if (!relevantJob || relevantJob?.state === 'completed') {
    return PM_ENTITIES_NO_DATA_MESSAGE;
  }
  return relevantJob.state === 'failed'
    ? PM_ENTITY_SYNC_FAILED_MESSAGE
    : PM_ENTITIES_SYNC_IN_PROGRESS_MESSAGE;
};

export const unionEntitiesWithPMImportSettings = (
  filtersFromPmImportSettings = [],
  currentFilters = [],
  entityType
) => {
  const result = _.unionBy(
    filtersFromPmImportSettings,
    currentFilters,
    item => item.id || item.provider_id
  );
  return result.map(item => ({
    provider_id: item.id || item.provider_id,
    ...(entityType === FILTER_TYPES.EPIC && (item.title || item.provider_key)
      ? { provider_key: item.title || item.provider_key }
      : {})
  }));
};

// here we are fetching all entities by entity type.
// DDB returns with limit of 100
// if not all entities returned, we fetching the next 100
export const getAllEntitiesByEntityType = async (sensorId, entityType) => {
  const url = mapProviderEntityToUrl[sensorId][entityType];
  if (!url) {
    return { items: [], totalCount: 0 };
  }
  try {
    const { totalCount = 0 } = (await axios.get(`${url}?limit=1`)) || {}; // execute call for totalCount

    const allEntitiesResponse =
      (await axios.get(`${url}?limit=${totalCount}`)) || {};
    return allEntitiesResponse;
  } catch (e) {
    window.TrackJS && window.TrackJS.track(e);
    return { items: [], totalCount: 0 };
  }
};

export const isSelectedFiltersObjectEmpty = filters => {
  return (
    _.isEmpty(filters) || Object.values(filters).every(arr => arr.length === 0)
  );
};
